import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import Hls from "hls.js";
import { Box, IconButton, CircularProgress, useMediaQuery, Slider } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import SettingsIcon from "@mui/icons-material/Settings";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeDownIcon from "@mui/icons-material/VolumeDown";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeMuteIcon from "@mui/icons-material/VolumeMute";
import { STREAMING_URL, BASE_URL } from "../../constant/apiUrl";
import fetchAndCacheImage from "../../utils/fetchandCatchImages";
import { useLocation } from "react-router-dom";
import { parseTimeParam, formatTime, formatTimeHHMMSS, parseChaptersAPI } from "./helpers";
import "./VideoScreenStyle.css";
import { getAPI } from "../../API/ApiService";
import defaultThumbnail from "../../assets/Thumbnail_default.jpg";

function VideoPlayer({ documentData }) {
    const videoRef = useRef(null);
    const progressRef = useRef(null);
    const containerRef = useRef(null);
    const token = localStorage.getItem("auth_token");

    const [poster, setPoster] = useState(null);
    const [loading, setLoading] = useState(false);
    const [hasPlayed, setHasPlayed] = useState(false);
    const [playButtonClicked, setPlayButtonClicked] = useState(false);
    const [showPoster, setShowPoster] = useState(true);

    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [hoverTooltip, setHoverTooltip] = useState(null);

    const [chapters, setChapters] = useState([]);
    const [fileName, setFileName] = useState(null);

    const [subtitleTracks, setSubtitleTracks] = useState([]);
    const [activeSubtitle, setActiveSubtitle] = useState(-1);
    const [currentSubtitleText, setCurrentSubtitleText] = useState('');

    const [playbackRate, setPlaybackRate] = useState(1);
    const [showSettings, setShowSettings] = useState(false);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);
    const [showVolumeSlider, setShowVolumeSlider] = useState(false);

    const [hlsInstance, setHlsInstance] = useState(null);

    const [showControls, setShowControls] = useState(true);
    const hideTimeoutRef = useRef(null);
    const lastSeekTimeRef = useRef(null);
    const playVideoRef = useRef(null);
    const [subtitlePosition, setSubtitlePosition] = useState('default');
    const [isSeeking, setIsSeeking] = useState(false);
    const isInitialMountRef = useRef(true);
    const initialSeekCompletedRef = useRef(false);
    const videoStartedPlayingRef = useRef(false);
    const chapterClickTriggeredLoadRef = useRef(false);

    const isMobile = useMediaQuery("(max-width:600px)");
    const location = useLocation();
    const [seekTime, setSeekTime] = useState(null);

    // Memoized values
    const masterUrl = useMemo(() => 
        fileName ? `${STREAMING_URL}/fritolay/video/hls/${fileName}` : null, 
        [fileName]
    );

    const currentChapter = useMemo(() => {
        if (chapters.length === 0 || duration === 0) return null;
        
        // Check if we're past the last chapter's endTimestamp
        const lastChapter = chapters[chapters.length - 1];
        // Only apply endTimestamp check if it exists and is valid (greater than 0)
        if (lastChapter && lastChapter.endTimestamp !== null && lastChapter.endTimestamp !== undefined && lastChapter.endTimestamp > 0) {
            if (lastChapter.endTimestamp < duration && currentTime > lastChapter.endTimestamp) {
                // Past the last chapter's endTimestamp - return null
                return null;
            }
        }
        
        return chapters.find((chapter, index) => {
            const nextChapter = chapters[index + 1];
            return currentTime >= chapter.seconds && 
                   (!nextChapter || currentTime < nextChapter.seconds);
        });
    }, [chapters, currentTime, duration]);

    // ✅ Parse query/hash time params
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tParam = params.get("t");
        let time = parseTimeParam(tParam);
        if (time === null && location.hash.startsWith("#chapter=")) {
            const hashValue = location.hash.replace("#chapter=", "");
            time = parseTimeParam(hashValue);
        }
        setSeekTime(time);
    }, [location.search, location.hash]);

    // ✅ Simple seek function - only seek when video is ready
    const performSeek = useCallback((targetTime) => {
        if (!videoRef.current || targetTime === null) return;
        
        const video = videoRef.current;
        
        // Only seek if video has duration and is ready
        if (video.duration && targetTime <= video.duration && video.readyState >= 2) {
            video.currentTime = targetTime;
            lastSeekTimeRef.current = targetTime;
        }
    }, []);

    // ✅ Listen for chapter click events (even when URL doesn't change)
    useEffect(() => {
        const handleChapterClick = (event) => {
            const { timeInSeconds } = event.detail || {};
            if (timeInSeconds !== undefined && timeInSeconds !== null) {
                const currentVideoTime = videoRef.current?.currentTime || 0;
                const isDifferentTime = Math.abs(currentVideoTime - timeInSeconds) > 0.5; 
                
                // Only seek if timestamp is different, or if it's the same but we want to restart
                if (isDifferentTime || (hasPlayed && initialSeekCompletedRef.current)) {
                    // Reset last seek time to allow seeking
                    if (!isDifferentTime) {
                        // Same timestamp - reset to force restart
                        lastSeekTimeRef.current = null;
                    }
                    
                    // Update seekTime - this will trigger the seek effect
                    setSeekTime(timeInSeconds);
                    
                    // If video is already loaded, immediately seek and play
                    if (hasPlayed && videoRef.current && videoRef.current.readyState >= 2) {
                        const video = videoRef.current;
                        const videoDuration = video.duration || 0;
                        
                        // Check if chapter time exceeds video duration
                        if (videoDuration > 0 && timeInSeconds > videoDuration) {
                            // Chapter time exceeds video duration - start from beginning
                            console.warn(`Chapter time ${timeInSeconds}s exceeds video duration ${videoDuration}s. Starting from beginning.`);
                            const startTime = 0;
                            
                            if (initialSeekCompletedRef.current) {
                                setIsSeeking(true);
                            }
                            
                            performSeek(startTime);
                            
                            // Start playing
                            if (initialSeekCompletedRef.current) {
                                if (video.paused) {
                                    video.play().then(() => {
                                        setIsPlaying(true);
                                    }).catch((error) => {
                                        console.log("Play failed after chapter click:", error);
                                    });
                                } else {
                                    setIsPlaying(true);
                                }
                            }
                        } else {
                            // Normal seek within video duration
                            // Show loader
                            if (initialSeekCompletedRef.current) {
                                setIsSeeking(true);
                            }
                            
                            // If same timestamp, force seek by temporarily going slightly before target
                            if (!isDifferentTime && Math.abs(video.currentTime - timeInSeconds) < 0.5) {
                                video.currentTime = Math.max(0, timeInSeconds - 0.1);
                            }
                            
                            // Seek to target time
                            performSeek(timeInSeconds);
                            
                            // Start playing
                            if (initialSeekCompletedRef.current) {
                                if (video.paused) {
                                    video.play().then(() => {
                                        setIsPlaying(true);
                                    }).catch((error) => {
                                        console.log("Play failed after chapter click:", error);
                                    });
                                } else {
                                    setIsPlaying(true);
                                }
                            }
                        }
                    }
                }
            }
        };

        window.addEventListener('chapterClick', handleChapterClick);
        return () => {
            window.removeEventListener('chapterClick', handleChapterClick);
        };
    }, [hasPlayed, performSeek]);

    // ✅ Additional listener for URL changes (fallback)
    useEffect(() => {
        const handlePopState = () => {
            const params = new URLSearchParams(window.location.search);
            const tParam = params.get("t");
            if (tParam) {
                const time = parseTimeParam(tParam);
                if (time !== null) {
                    setSeekTime(time);
                }
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    // ✅ Seek when seekTime changes (for chapter clicks)
    useEffect(() => {
        if (seekTime === null) return;
        
        // If video hasn't been loaded yet and URL has timestamp, always load it
        if (!hasPlayed && documentData?.fileId && fileName && masterUrl && !hlsInstance) {
            // Check if this is a chapter click or URL with timestamp
            const isChapterClick = chapterClickTriggeredLoadRef.current;
            const hasTimestampInUrl = seekTime !== null;
            
            // If it's a chapter click or URL has timestamp, mark for auto-play
            if (isChapterClick || hasTimestampInUrl) {
                chapterClickTriggeredLoadRef.current = true;
            }
            
            // Load the video first
            if (playVideoRef.current) {
                playVideoRef.current();
            }
            
            // Wait for video to load and then seek and play
            // Note: The MANIFEST_PARSED handler will also handle seeking and playing,
            // but this is a fallback in case the video is ready before manifest is parsed
            const seekAndPlayAfterLoad = () => {
                if (videoRef.current && videoRef.current.readyState >= 2 && videoRef.current.duration) {
                    const video = videoRef.current;
                    const videoDuration = video.duration || 0;
                    
                    // Video is ready, seek to timestamp if available
                    if (seekTime !== null) {
                        // Check if seek time exceeds video duration
                        if (videoDuration > 0 && seekTime > videoDuration) {
                            // Seek time exceeds video duration - start from beginning
                            console.warn(`Seek time ${seekTime}s exceeds video duration ${videoDuration}s. Starting from beginning.`);
                            const startTime = 0;
                            performSeek(startTime);
                        } else {
                            performSeek(seekTime);
                        }
                    }
                    // Always start playing if there's a timestamp (for both chapter clicks and URL with timestamp)
                    if (seekTime !== null) {
                        video.play().then(() => {
                            setIsPlaying(true);
                        }).catch((error) => {
                            console.log("Play failed:", error);
                        });
                    }
                } else if (!hasPlayed) {
                    // Video not ready yet, check again
                    setTimeout(seekAndPlayAfterLoad, 100);
                }
            };
            
            // Start checking after a short delay to allow video to start loading
            setTimeout(seekAndPlayAfterLoad, 200);
            return;
        }
        
        // Video is already loaded - seek and play
        if (hasPlayed && videoRef.current && videoRef.current.readyState >= 2) {
            const video = videoRef.current;
            const videoDuration = video.duration || 0;
            const currentVideoTime = video?.currentTime || 0;
            const isDifferentTime = Math.abs(currentVideoTime - seekTime) > 0.5; 
            
            const isUserInitiatedSeek = hasPlayed && initialSeekCompletedRef.current;
            const shouldSeek = isDifferentTime || (isUserInitiatedSeek && lastSeekTimeRef.current !== seekTime);
            
            if (!shouldSeek) {
                return;
            }
            
            // Check if seek time exceeds video duration
            if (videoDuration > 0 && seekTime > videoDuration) {
                // Seek time exceeds video duration - start from beginning
                console.warn(`Seek time ${seekTime}s exceeds video duration ${videoDuration}s. Starting from beginning.`);
                const startTime = 0;
                
                if (initialSeekCompletedRef.current) {
                    setIsSeeking(true);
                }
                
                performSeek(startTime);
                
                const shouldAutoPlay = initialSeekCompletedRef.current || (!initialSeekCompletedRef.current && seekTime !== null);
                
                if (shouldAutoPlay) {
                    if (video.paused) {
                        video.play().then(() => {
                            setIsPlaying(true);
                        }).catch((error) => {
                            console.log("Play failed:", error);
                        });
                    } else {
                        setIsPlaying(true);
                    }
                }
            } else {
                // Normal seek within video duration
                if (initialSeekCompletedRef.current) {
                    setIsSeeking(true);
                }
                
                if (isUserInitiatedSeek && lastSeekTimeRef.current === seekTime && video.currentTime === seekTime) {
                    video.currentTime = Math.max(0, seekTime - 0.1);
                }
                
                performSeek(seekTime);
                
                const shouldAutoPlay = initialSeekCompletedRef.current || (!initialSeekCompletedRef.current && seekTime !== null);
                
                if (shouldAutoPlay) {
                    if (video.paused) {
                        video.play().then(() => {
                            setIsPlaying(true);
                        }).catch((error) => {
                            console.log("Play failed:", error);
                        });
                    } else {
                        setIsPlaying(true);
                    }
                }
            }
        }
    }, [seekTime, performSeek, hasPlayed, documentData?.fileId, fileName, masterUrl, hlsInstance]);

    // Helper function to check if a string is a valid URL
    const isValidUrl = (string) => {
        if (!string || typeof string !== "string") return false;
        try {
            const url = new URL(string);
            return url.protocol === "http:" || url.protocol === "https:";
        } catch (_) {
            return false;
        }
    };

    // ✅ Poster - Use CDN URL if available, otherwise fetch by file ID, fallback to default thumbnail
    useEffect(() => {
        const fetchPoster = async () => {
            if (!documentData) {
                setPoster(defaultThumbnail);
                setShowPoster(true);
                return;
            }
            setShowPoster(true);

            const cdnUrl = documentData?.cdnUrl;
            const thumbnailFileId = documentData?.thumbnailFileId;

            // If cdnUrl is a valid URL, use it directly (no API call needed)
            if (isValidUrl(cdnUrl)) {
                setPoster(cdnUrl);
                return;
            }

            // If cdnUrl is not a valid URL, try to fetch using thumbnailFileId
            if (thumbnailFileId) {
                try {
                    const url = await fetchAndCacheImage(thumbnailFileId);
                    setPoster(url);
                } catch (error) {
                    console.error("Error fetching poster image:", error);
                    // Use default thumbnail if fetch fails
                    setPoster(defaultThumbnail);
                }
            } else {
                // Use default thumbnail when no thumbnail is available
                setPoster(defaultThumbnail);
            }
        };
        fetchPoster();
    }, [documentData]);

    // ✅ Fetch file details (get fileName)
    useEffect(() => {
        if (!documentData?.fileId) return;
        const fetchFileDetails = async () => {
            try {
                const response = await getAPI(`${BASE_URL}/file-detail/find-file-detail?fileId=${documentData.fileId}&isStreaming=true`);
                const data = await response;
                if (data?.data?.fileName) {
                    const baseName = data?.data?.fileName.replace(/\.[^.]+$/, "");
                    setFileName(`${baseName}.m3u8`);
                }
            } catch (error) {
                console.error("Error fetching file details:", error);
            }
        };
        fetchFileDetails();
    }, [documentData, token]);

    // ✅ Auto-load video when URL has timestamp and fileName is available
    useEffect(() => {
        if (seekTime !== null && !hasPlayed && documentData?.fileId && fileName && masterUrl && !hlsInstance && isInitialMountRef.current) {
            chapterClickTriggeredLoadRef.current = true;
            if (playVideoRef.current) {
                playVideoRef.current();
            }
        }
    }, [seekTime, hasPlayed, documentData?.fileId, fileName, masterUrl, hlsInstance]);

    // ✅ Play / Pause toggle
    const togglePlay = useCallback(() => {
        if (!videoRef.current) return;
        if (videoRef.current.paused) {
            setLoading(true);
            // If there's a seekTime from URL and we haven't sought to it yet, seek first
            if (seekTime !== null && lastSeekTimeRef.current !== seekTime) {
                // Wait for video to be ready before seeking
                const checkAndSeek = () => {
                    if (videoRef.current && videoRef.current.readyState >= 2) {
                        performSeek(seekTime);
                        // Play after seeking
                        videoRef.current.play().then(() => {
                            setIsPlaying(true);
                            // Loading will be set to false by the 'playing' event listener
                        }).catch((error) => {
                            console.log("Play failed:", error);
                            setLoading(false);
                        });
                    } else {
                        setTimeout(checkAndSeek, 100);
                    }
                };
                checkAndSeek();
            } else {
                // No seek time, just play
                videoRef.current.play().then(() => {
                    setIsPlaying(true);
                    // Loading will be set to false by the 'playing' event listener
                }).catch((error) => {
                    console.log("Play failed:", error);
                    setLoading(false);
                });
            }
        } else {
            videoRef.current.pause();
            setIsPlaying(false);
        }
    }, [seekTime, performSeek]);

    // ✅ Fullscreen toggle
    const toggleFullscreen = useCallback(() => {
        if (!containerRef.current) return;
        if (document.fullscreenElement) {
            document.exitFullscreen();
        } else {
            containerRef.current.requestFullscreen();
        }
    }, []);

    // ✅ Track progress and time
    useEffect(() => {
        if (!videoRef.current) return;

        const update = () => {
            if (!videoRef.current) return;
            const video = videoRef.current;
            const currentVideoTime = video.currentTime || 0;
            const videoDuration = video.duration || 0;
            setCurrentTime(currentVideoTime);
            setDuration(videoDuration);
            setProgress(videoDuration ? (currentVideoTime / videoDuration) * 100 : 0);
            setIsPlaying(!video.paused);
            
            // Check if last chapter's endTimestamp is before video duration and reset hover tooltip if exceeded
            // Note: Subtitles should always show regardless of chapter endTimestamp
            if (chapters.length > 0 && videoDuration > 0) {
                const lastChapter = chapters[chapters.length - 1];
                // Only apply endTimestamp check if it exists and is valid (greater than 0)
                if (lastChapter && lastChapter.endTimestamp !== null && lastChapter.endTimestamp !== undefined && lastChapter.endTimestamp > 0) {
                    // Last chapter has an endTimestamp
                    if (lastChapter.endTimestamp < videoDuration && currentVideoTime > lastChapter.endTimestamp) {
                        // Video time has exceeded the last chapter's endTimestamp - clear hover tooltip only
                        setHoverTooltip(prev => prev ? { ...prev, text: '' } : null);
                    }
                }
            }
            
            // Mark initial seek as completed once video starts playing (for cases without initial seek)
            // This ensures loader shows on chapter clicks even if page loaded without ?t= parameter
            if (hasPlayed && !initialSeekCompletedRef.current && !videoStartedPlayingRef.current && video.currentTime > 0) {
                videoStartedPlayingRef.current = true;
                // Mark as completed after a short delay to distinguish from initial seek
                setTimeout(() => {
                    initialSeekCompletedRef.current = true;
                }, 1000);
            }
        };

        const handleSeeking = () => {
            // Video is seeking - show loader only if initial seek has been completed
            if (hasPlayed && initialSeekCompletedRef.current) {
                setIsSeeking(true);
            }
        };

        const handleSeeked = () => {
            // Video finished seeking
            setIsSeeking(false);
            // Mark initial seek as completed after first seek finishes
            if (!initialSeekCompletedRef.current) {
                initialSeekCompletedRef.current = true;
            }
        };

        const video = videoRef.current;
        
        // Handle video playing event - hide loader and poster when video actually starts playing
        const handlePlaying = () => {
            setLoading(false);
            setShowPoster(false);
        };

        // Handle video waiting event - show loader and poster if video is buffering
        const handleWaiting = () => {
            if (isPlaying) {
                setLoading(true);
                // Keep poster visible while buffering if it was visible before
                if (poster) {
                    setShowPoster(true);
                }
            }
        };

        video.addEventListener("timeupdate", update);
        video.addEventListener("loadedmetadata", update);
        video.addEventListener("seeking", handleSeeking);
        video.addEventListener("seeked", handleSeeked);
        video.addEventListener("playing", handlePlaying);
        video.addEventListener("waiting", handleWaiting);

        return () => {
            if (video) {
                video.removeEventListener("timeupdate", update);
                video.removeEventListener("loadedmetadata", update);
                video.removeEventListener("seeking", handleSeeking);
                video.removeEventListener("seeked", handleSeeked);
                video.removeEventListener("playing", handlePlaying);
                video.removeEventListener("waiting", handleWaiting);
            }
        };
    }, [hasPlayed, isPlaying, chapters]);

    // ✅ Handle progress bar click
    const handleProgressClick = useCallback((e) => {
        if (!videoRef.current || !progressRef.current) return;
        
        const rect = progressRef.current.getBoundingClientRect();
        const percent = (e.clientX - rect.left) / rect.width;
        const targetTime = percent * (videoRef.current.duration || duration);
        
        // If video hasn't been loaded yet, load and play it first
        if (!hasPlayed && documentData?.fileId && fileName && masterUrl && !hlsInstance) {
            if (playVideoRef.current) {
                playVideoRef.current();
            }
            // Wait for the video to load and then seek
            const seekAfterLoad = () => {
                if (videoRef.current && videoRef.current.duration) {
                    videoRef.current.currentTime = targetTime;
                    if (videoRef.current.paused) {
                        videoRef.current.play().catch(() => {});
                    }
                } else if (!hasPlayed) {
                    setTimeout(seekAfterLoad, 100);
                }
            };
            seekAfterLoad();
            return;
        }
        
        // Normal seek when video is loaded
        if (videoRef.current.duration) {
            videoRef.current.currentTime = targetTime;
            // If video is paused and user clicked, optionally start playing
            if (videoRef.current.paused && hasPlayed) {
                videoRef.current.play().catch(() => {});
            }
        }
    }, [hasPlayed, hlsInstance, documentData?.fileId, fileName, masterUrl, duration]);

    // ✅ Handle hover
    const handleProgressHover = useCallback((e) => {
        if (!videoRef.current || !progressRef.current) return;
        const rect = progressRef.current.getBoundingClientRect();
        
        // Check if mouse is actually within the progress bar bounds
        if (e.clientX < rect.left || e.clientX > rect.right || 
            e.clientY < rect.top || e.clientY > rect.bottom) {
            setHoverTooltip(null);
            return;
        }
        
        const percent = (e.clientX - rect.left) / rect.width;
        const hoverTime = percent * (videoRef.current.duration || 0);
        const videoDuration = videoRef.current.duration || 0;

        // Check if hover time is past the last chapter's endTimestamp
        let chapterTitle = '';
        if (chapters.length > 0 && videoDuration > 0) {
            const lastChapter = chapters[chapters.length - 1];
            // Only apply endTimestamp check if it exists and is valid (greater than 0)
            if (lastChapter && lastChapter.endTimestamp !== null && lastChapter.endTimestamp !== undefined && lastChapter.endTimestamp > 0) {
                if (lastChapter.endTimestamp < videoDuration && hoverTime > lastChapter.endTimestamp) {
                    // Past the last chapter's endTimestamp - don't show chapter title
                    chapterTitle = '';
                } else {
                    // Find the chapter for this hover time
                    const chapter = chapters.find(
                        (c, idx) =>
                            hoverTime >= c.seconds &&
                            (idx === chapters.length - 1 || hoverTime < chapters[idx + 1].seconds)
                    );
                    chapterTitle = chapter?.title || '';
                }
            } else {
                // No endTimestamp, use normal logic
                const chapter = chapters.find(
                    (c, idx) =>
                        hoverTime >= c.seconds &&
                        (idx === chapters.length - 1 || hoverTime < chapters[idx + 1].seconds)
                );
                chapterTitle = chapter?.title || '';
            }
        }

        setHoverTooltip({
            x: e.clientX - rect.left,
            text: chapterTitle,
            time: formatTimeHHMMSS(hoverTime),
        });
    }, [chapters]);

    // ✅ Subtitles
    const setSubtitleTrack = (id) => {
        if (!hlsInstance) return;
        hlsInstance.subtitleTrack = id;
        setActiveSubtitle(id);
        
        // Enable subtitle display for HLS
        if (id !== -1) {
            hlsInstance.subtitleDisplay = true;
        } else {
            hlsInstance.subtitleDisplay = false;
        }
    };

    // ✅ Speed
    const handleSpeedChange = (rate) => {
        setPlaybackRate(rate);
        if (videoRef.current) {
            videoRef.current.playbackRate = rate;
        }
    };

    // ✅ Volume control
    const handleVolumeChange = useCallback((event, newValue) => {
        const newVolume = newValue / 100;
        setVolume(newVolume);
        setIsMuted(newVolume === 0);
        if (videoRef.current) {
            videoRef.current.volume = newVolume;
            videoRef.current.muted = newVolume === 0;
        }
    }, []);

    const toggleMute = useCallback(() => {
        if (videoRef.current) {
            const newMuted = !isMuted;
            setIsMuted(newMuted);
            videoRef.current.muted = newMuted;
            if (newMuted) {
                // Store previous volume before muting
                if (volume > 0) {
                    videoRef.current.volume = volume;
                }
            } else {
                // Restore volume if it was 0, set to 0.5
                if (volume === 0) {
                    const newVolume = 0.5;
                    setVolume(newVolume);
                    videoRef.current.volume = newVolume;
                }
            }
        }
    }, [isMuted, volume]);

    // ✅ Sync volume with video element
    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.volume = volume;
            videoRef.current.muted = isMuted;
        }
    }, [volume, isMuted]);

    // ✅ Load video
    const playVideo = useCallback(() => {
        if (!documentData?.fileId || !fileName || !masterUrl) return;
        setPlayButtonClicked(true); // Hide play button instantly
        setLoading(true);
        if (Hls.isSupported()) {
            const hls = new Hls({
                xhrSetup: (xhr) => {
                    xhr.setRequestHeader("Authorization", `Bearer ${token}`);
                },
            });
            hls.attachMedia(videoRef.current);
            hls.loadSource(masterUrl);

            setHlsInstance(hls);
            console.log("hls", hls);
            hls.on(Hls.Events.SUBTITLE_TRACKS_UPDATED, (_, data) => {
                console.log("data", data);
                setSubtitleTracks(
                    data.subtitleTracks.map((t, idx) => ({
                        id: idx,
                        label: t.name,
                        lang: t.lang,
                    }))
                );
                if (data.subtitleTracks.length > 0) {
                    hls.subtitleDisplay = true;
                }
            });

            hls.on(Hls.Events.MANIFEST_PARSED, async () => {
                try {
                    const pathParts = location.pathname.split('/');
                    const videoDetailsIndex = pathParts.indexOf('video-details');
                    const contentId = videoDetailsIndex !== -1 && pathParts[videoDetailsIndex + 1] ? pathParts[videoDetailsIndex + 1] : null;
                    const videoFileId = documentData?.fileId;
                    
                    if (!contentId || !videoFileId) {
                        console.warn("Missing contentId or videoFileId for chapters API:", { contentId, videoFileId });
                        return;
                    }
                    
                    const chaptersUrl = `${BASE_URL}/video/${contentId}/${videoFileId}/chapters`;
                    // const res = await fetch(chaptersUrl, { 
                    //     headers: { 
                    //         'Authorization': `Bearer ${token}`,
                    //         'Accept': '*/*'
                    //     } 
                    // });
                    const res = await getAPI(chaptersUrl);
                    console.log("res", res);
                    if (res.status === 200) {
                        const chaptersData = await res.data;
                        setChapters(parseChaptersAPI(chaptersData));
                    } 
                } catch (err) {
                    console.error("Failed to fetch chapters:", err);
                }
                
                setHasPlayed(true);
                // Don't set loading to false here - wait for video to actually start playing
                
                // Mark initial mount as complete after video has loaded
                isInitialMountRef.current = false;
                
                // Check if this video load was triggered by a chapter click or URL timestamp
                const wasTriggeredByTimestamp = chapterClickTriggeredLoadRef.current;
                chapterClickTriggeredLoadRef.current = false; // Reset the flag
                
                // Auto-play after manifest is loaded
                if (videoRef.current) {
                    const video = videoRef.current;
                    const videoDuration = video.duration || 0;
                    
                    if (seekTime !== null && video.readyState >= 2) {
                        // URL has timestamp (from chapter click or direct URL) - seek to that time and play
                        // Check if seek time exceeds video duration
                        if (videoDuration > 0 && seekTime > videoDuration) {
                            // Seek time exceeds video duration - start from beginning
                            console.warn(`Seek time ${seekTime}s exceeds video duration ${videoDuration}s. Starting from beginning.`);
                            const startTime = 0;
                            performSeek(startTime);
                        } else {
                            performSeek(seekTime);
                        }
                        video.play().then(() => {
                            console.log(wasTriggeredByTimestamp ? "Video started playing after chapter click" : "Video started playing from URL timestamp");
                            setIsPlaying(true);
                            // Loading will be set to false by the 'playing' event listener
                        }).catch((error) => {
                            console.error("Auto-play failed:", error);
                            setLoading(false);
                        });
                    } else {
                        // No timestamp - just play from start
                        video.play().then(() => {
                            console.log("Video started playing successfully");
                            setIsPlaying(true);
                            // Loading will be set to false by the 'playing' event listener
                        }).catch((error) => {
                            console.error("Auto-play failed:", error);
                            setLoading(false);
                        });
                    }
                }
            });

            hls.on(Hls.Events.ERROR, (_, data) => {
                console.error("HLS error:", data);
                setLoading(false);
            });
        } else {
            if (!videoRef.current) return;
            videoRef.current.src = masterUrl;
            videoRef.current.play().then(() => {
                setHasPlayed(true);
                setIsPlaying(true);
                // Loading will be set to false by the 'playing' event listener
                // Mark initial mount as complete after video has loaded
                isInitialMountRef.current = false;
            }).catch((error) => {
                console.error("Play failed:", error);
                setLoading(false);
            });
        }
    }, [documentData?.fileId, fileName, masterUrl, token, location.pathname, seekTime, performSeek]);

    // ✅ Store playVideo in ref for use in handleProgressClick
    useEffect(() => {
        playVideoRef.current = playVideo;
    }, [playVideo]);

    // ✅ Auto-hide controls
    const resetHideTimer = useCallback(() => {
        setShowControls(true);
        if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = setTimeout(() => setShowControls(false), 4000);
    }, []);

    // ✅ Update subtitle position based on controls visibility
    useEffect(() => {
        setSubtitlePosition(showControls ? 'lifted' : 'default');
    }, [showControls]);

    // ✅ Apply subtitle positioning to video element
    useEffect(() => {
        if (!videoRef.current) return;
        const video = videoRef.current;
        const track = video.textTracks?.[0];
        if (track && activeSubtitle !== -1) {
            video.style.setProperty('--subtitle-bottom', subtitlePosition === 'lifted' ? '80px' : '20px');
        }
    }, [subtitlePosition, activeSubtitle]);

    // ✅ Track subtitle text changes for HLS
    useEffect(() => {
        if (!hlsInstance || activeSubtitle === -1) {
            console.log('No HLS instance or subtitle disabled');
            setCurrentSubtitleText('');
            return;
        }
        // const handleSubtitleFragParsed = (event, data) => {
            
        //     if (data.frag && data.frag.elementaryStreams) {
        //         // This is where we would extract subtitle text from HLS fragments
        //         // For now, we'll rely on the native subtitle rendering
        //     }
        // };

        // const handleSubtitleTracksUpdated = (event, data) => {
        //     console.log('Subtitle tracks updated:', data);
        // };

        // Try to get subtitle text directly from HLS
        const tryGetSubtitleText = () => {
            if (hlsInstance.subtitleTracks && hlsInstance.subtitleTracks[activeSubtitle]) {
                const track = hlsInstance.subtitleTracks[activeSubtitle];
                if (track.details && track.details.fragments) {
                    console.log('Track has fragments:', track.details.fragments.length);
                }
            }
        };

        // hlsInstance.on(Hls.Events.SUBTITLE_FRAG_PARSED, handleSubtitleFragParsed);
        // hlsInstance.on(Hls.Events.SUBTITLE_TRACKS_UPDATED, handleSubtitleTracksUpdated);

        // Try to get subtitle text immediately
        tryGetSubtitleText();

        return () => {
            // hlsInstance.off(Hls.Events.SUBTITLE_FRAG_PARSED, handleSubtitleFragParsed);
            // hlsInstance.off(Hls.Events.SUBTITLE_TRACKS_UPDATED, handleSubtitleTracksUpdated);
        };
    }, [hlsInstance, activeSubtitle]);

    // ✅ Alternative approach: Monitor native text tracks for HLS subtitles
    useEffect(() => {
        if (!videoRef.current || activeSubtitle === -1) {
            console.log('No video ref or subtitle disabled, clearing text');
            setCurrentSubtitleText('');
            return;
        }
        const video = videoRef.current;
        let retryCount = 0;
        const maxRetries = 50;
        const checkTextTracks = () => {
            const textTracks = video.textTracks;
            if (!textTracks || textTracks.length === 0) {
                if (retryCount < maxRetries) {
                    retryCount++;
                    setTimeout(checkTextTracks, 100);
                    return;
                } else {
                    console.log('Max retries reached, no text tracks found');
                    return;
                }
            }
            for (let i = 0; i < textTracks.length; i++) {
                console.log(`Track ${i}:`, {
                    mode: textTracks[i].mode,
                    kind: textTracks[i].kind,
                    label: textTracks[i].label,
                    language: textTracks[i].language,
                    activeCues: textTracks[i].activeCues?.length || 0
                });
            }
            let activeTrack = null;
            for (let i = 0; i < textTracks.length; i++) {
                if (textTracks[i].mode === 'showing') {
                    activeTrack = textTracks[i];
                    break;
                }
            }
            if (!activeTrack) {
                if (textTracks.length > 0) {
                    textTracks[0].mode = 'showing';
                    activeTrack = textTracks[0];
                }
            }
            if (!activeTrack) {
                return;
            }
            const handleCueChange = () => {
                // Subtitles should always show regardless of chapter endTimestamp
                // Only display subtitles based on active cues
                const activeCues = activeTrack.activeCues;
                if (activeCues && activeCues.length > 0) {
                    const cue = activeCues[0];
                    const subtitleText = cue.text || '';
                    setCurrentSubtitleText(subtitleText);
                } else {
                    setCurrentSubtitleText('');
                }
            };
            activeTrack.addEventListener('cuechange', handleCueChange);
            handleCueChange();  
            return () => {
                activeTrack.removeEventListener('cuechange', handleCueChange);
            };
        };

        const cleanup = checkTextTracks();
        return cleanup;
    }, [activeSubtitle, chapters]);

    // ✅ Additional approach: Monitor video element for subtitle changes
    useEffect(() => {
        if (!videoRef.current || activeSubtitle === -1) return;
        const checkForSubtitles = () => {
            const subtitleElements = document.querySelectorAll('video::cue, .subtitle, [data-subtitle]');
        };
        const interval = setInterval(checkForSubtitles, 5000);
        
        return () => {
            clearInterval(interval);
        };
    }, [activeSubtitle]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        container.addEventListener("mousemove", resetHideTimer);
        container.addEventListener("click", resetHideTimer);
        resetHideTimer();
        return () => {
            container.removeEventListener("mousemove", resetHideTimer);
            container.removeEventListener("click", resetHideTimer);
            if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
        };
    }, []);

    // ✅ Handle video area click (above progress bar) to toggle play/pause
    const handleVideoAreaClick = useCallback((e) => {
        const target = e.target;
        const isControlClick = target.closest('[data-controls]') || 
                              target.closest('button') || 
                              target.closest('[role="button"]') ||
                              target.closest('[data-testid="play-video"]') ||
                              progressRef.current?.contains(target);
        
        if (isControlClick) {
            return; 
        }

        if (hasPlayed && videoRef.current) {
            const container = containerRef.current;
            if (container) {
                const rect = container.getBoundingClientRect();
                const clickY = e.clientY - rect.top;
                const containerHeight = rect.height;
                const controlsAreaHeight = 100;
                
                if (clickY < containerHeight - controlsAreaHeight) {
                    togglePlay();
                }
            }
        }
    }, [hasPlayed, togglePlay]);

    return (
        <Box
                ref={containerRef}
                data-testid="video-player"
                onClick={handleVideoAreaClick}
                sx={{
                    position: "relative",
                    width: "100%",
                    height: isMobile ? "200px" : "667px",
                    background: "black",
                }}
            >
            <video 
                ref={videoRef} 
                playsInline 
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
                className={`video-player ${subtitlePosition === 'lifted' ? 'controls-visible' : 'controls-hidden'}`}
            />

            {/* Subtitle Container */}
            <Box
                className={`subtitle-container ${subtitlePosition}`}
            >
                {currentSubtitleText && (
                    <Box className="subtitle-text">
                        {currentSubtitleText}
                    </Box>
                )}
            </Box>

            {/* Poster - Show until video starts playing */}
            {showPoster && poster && (
                <Box
                    data-testid="thumbnail"
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundImage: `url(${poster})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        zIndex: 2,
                    }}
                />
            )}

            {/* Loading Overlay - Show on top of poster until video starts playing */}
            {loading && (
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: showPoster && poster ? "rgba(0, 0, 0, 0.5)" : "rgba(0, 0, 0, 0.7)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 10,
                    }}
                >
                    <CircularProgress size={60} sx={{ color: "white" }} />
                </Box>
            )}

            {/* Seeking Loader Overlay */}
            {isSeeking && (
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: "rgba(0, 0, 0, 0.7)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 10,
                    }}
                >
                    <CircularProgress size={60} sx={{ color: "white" }} />
                </Box>
            )}

            {/* Play button */}
            {!hasPlayed && !playButtonClicked && (
                <IconButton
                    data-testid="play-video"
                    onClick={playVideo}
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        background: "black",
                        color: "white",
                        width: 80,
                        height: 80,
                        zIndex: 15,
                        borderRadius: "50%",
                        "&:hover": {
                            background: "rgba(0,0,0,0.8)",
                        },
                    }}
                >
                    <Box
                        sx={{
                            width: 0,
                            height: 0,
                            borderLeft: "20px solid white",
                            borderTop: "12px solid transparent",
                            borderBottom: "12px solid transparent",
                            marginLeft: "4px",
                        }}
                    />
                </IconButton>
            )}


            {/* Progress bar - show when video metadata is loaded, even before playing */}
            {duration > 0 && showControls && (
                <Box
                    data-controls
                    sx={{
                        position: "absolute",
                        bottom: hasPlayed ? 0 : "50px",
                        left: 0,
                        right: 0,
                        background: "rgba(0,0,0,0.7)",
                        color: "white",
                        padding: "8px",
                        transition: "opacity 0.3s",
                    }}
                >
                    {/* Progress bar */}
                    <Box
                        ref={progressRef}
                        sx={{ position: "relative", height: "6px", background: "#555", cursor: "pointer" }}
                        onClick={handleProgressClick}
                        onMouseMove={handleProgressHover}
                        onMouseLeave={() => setHoverTooltip(null)}
                    >
                        {/* Chapter segments */}
                        {chapters.map((c, idx) => {
                            const start = c.seconds;
                            const end = idx < chapters.length - 1 ? chapters[idx + 1].seconds : duration;
                            const startPercent = duration ? (start / duration) * 100 : 0;
                            const widthPercent = duration ? ((end - start) / duration) * 100 : 0;
                            return (
                                <Box
                                    key={idx}
                                    sx={{
                                        position: "absolute",
                                        left: `${startPercent}%`,
                                        width: `${widthPercent}%`,
                                        height: "100%",
                                        background: idx % 2 === 0 ? "rgba(255,255,255,0.15)" : "rgba(255,255,255,0.25)",
                                        borderRight: "2px solid black",
                                    }}
                                />
                            );
                        })}

                        {/* Red progress overlay */}
                        <Box sx={{ width: `${progress}%`, height: "100%",  position: "absolute", top: 0, left: 0, background: progress % 2 === 0 ? "rgba(255,255,255)" : "rgba(238, 9, 9, 0.7)",}} />

                        {/* Hover tooltip */}
                        {hoverTooltip && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    bottom: "120%",
                                    left: hoverTooltip.x,
                                    transform: "translateX(-50%)",
                                    background: "black",
                                    padding: "2px 6px",
                                    fontSize: "14px",
                                    borderRadius: "4px",
                                }}
                            >
                                {`${hoverTooltip.text} ${hoverTooltip.text !== '' ? '|' : ''} ${hoverTooltip.time}`}
                            </Box>
                        )}
                    </Box>

                    {/* Control row - only show when video has played */}
                    {hasPlayed && (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", marginTop: "4px", position: "relative", gap: "16px" }}>
                            <IconButton onClick={togglePlay} sx={{ color: "white" }}>
                                {isPlaying ? <PauseIcon sx={{ fontSize: "30px" }} /> : <PlayArrowIcon sx={{ fontSize: "30px" }} />}
                            </IconButton>
                            <Box sx={{ color: "white", fontSize: "14px" }}>{formatTime(currentTime)} / {formatTime(duration)}</Box>
                            <Box 
                                sx={{ 
                                    color: "white", 
                                    fontSize: "14px",
                                    display: { xs: "none", md: "flex" },
                                    alignItems: "center",
                                    gap: "4px",
                                    // cursor: "pointer",
                                    padding: "4px 8px",
                                    borderRadius: "4px",
                                    "&:hover": {
                                        backgroundColor: "rgba(255, 255, 255, 0.1)"
                                    }
                                }}
                                onClick={() => {
                                    window.dispatchEvent(new CustomEvent('toggleChapters'));
                                }}
                            >
                                {currentChapter?.title}
                            </Box>

                            <Box sx={{ marginLeft: "auto", display: "flex", gap: "8px", position: "relative", alignItems: "center" }}>
                                {/* Volume Control */}
                                <Box 
                                    sx={{ 
                                        display: "flex", 
                                        alignItems: "center", 
                                        gap: "4px",
                                        position: "relative"
                                    }}
                                    onMouseEnter={() => setShowVolumeSlider(true)}
                                    onMouseLeave={() => setShowVolumeSlider(false)}
                                >
                                    <IconButton onClick={toggleMute} sx={{ color: "white" }}>
                                        {isMuted || volume === 0 ? (
                                            <VolumeOffIcon sx={{ fontSize: "30px" }} />
                                        ) : volume < 0.5 ? (
                                            <VolumeDownIcon sx={{ fontSize: "30px" }} />
                                        ) : (
                                            <VolumeUpIcon sx={{ fontSize: "30px" }} />
                                        )}
                                    </IconButton>
                                    {showVolumeSlider && (
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                bottom: "45px",
                                                left: "50%",
                                                transform: "translateX(-50%)",
                                                background: "rgba(0,0,0,0.9)",
                                                padding: "8px 12px",
                                                borderRadius: "4px",
                                                width: "40px",
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                            }}
                                        >
                                            <Slider
                                                orientation="vertical"
                                                value={volume * 100}
                                                onChange={handleVolumeChange}
                                                min={0}
                                                max={100}
                                                sx={{
                                                    color: "white",
                                                    height: "100px",
                                                    margin: "0 auto",
                                                    '& .MuiSlider-thumb': {
                                                        width: 12,
                                                        height: 12,
                                                    },
                                                    '& .MuiSlider-track': {
                                                        width: 4,
                                                    },
                                                    '& .MuiSlider-rail': {
                                                        width: 4,
                                                        opacity: 0.3,
                                                    },
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                                <IconButton onClick={() => setShowSettings(!showSettings)} sx={{ color: "white" }}>
                                    <SettingsIcon sx={{ fontSize: "30px" }}/>
                                </IconButton>
                                <IconButton onClick={toggleFullscreen} sx={{ color: "white" }}>
                                    <FullscreenIcon sx={{ fontSize: "35px" }} />
                                </IconButton>
                                {showSettings && (
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            bottom: "35px",
                                            right: "0",
                                            background: "rgba(0,0,0,0.9)",
                                            color: "white",
                                            padding: "8px",
                                            borderRadius: "6px",
                                            zIndex: 5,
                                            minWidth: "150px",
                                        }}
                                    >
                                        <Box sx={{ marginBottom: "8px" }}>
                                            <strong>Speed</strong>
                                            {[0.5, 1, 1.5, 2].map((rate) => (
                                                <div
                                                    key={rate}
                                                    style={{
                                                        cursor: "pointer",
                                                        padding: "4px",
                                                        background: playbackRate === rate ? "rgba(255,255,255,0.2)" : "transparent",
                                                    }}
                                                    onClick={() => {
                                                        handleSpeedChange(rate);
                                                        setShowSettings(false);
                                                    }}
                                                >
                                                    {rate}x
                                                </div>
                                            ))}
                                        </Box>
                                        <Box>
                                            <strong>Subtitles</strong>
                                            <div
                                                style={{
                                                    cursor: "pointer",
                                                    padding: "4px",
                                                    background: activeSubtitle === -1 ? "rgba(255,255,255,0.2)" : "transparent",
                                                }}
                                                onClick={() => {
                                                    setSubtitleTrack(-1);
                                                    setShowSettings(false);
                                                }}
                                            >
                                                Off
                                            </div>
                                            {subtitleTracks.map((t) => (
                                                <div
                                                    key={t.id}
                                                    style={{
                                                        cursor: "pointer",
                                                        padding: "4px",
                                                        background: activeSubtitle === t.id ? "rgba(255,255,255,0.2)" : "transparent",
                                                    }}
                                                    onClick={() => {
                                                        setSubtitleTrack(t.id);
                                                        setShowSettings(false);
                                                    }}
                                                >
                                                    {`${t.label}`}
                                                </div>
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
}


export default VideoPlayer;
